import { ReactNode, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useAuth } from "../store/auth";
import LanguageSwitcher from "./LanguageSwitcher";
import Avatar from "./ui/Avatar";
import SubscriptionBanner from "./SubscriptionBanner";
import IgAccountAttentionBanner from "./IgAccountAttentionBanner";
import {
  IconAccounts,
  IconActivity,
  IconCampaigns,
  IconClose,
  IconDM,
  IconDashboard,
  IconMenu,
  IconSettings,
  IconShield,
  IconSignOut,
  IconTarget,
} from "./ui/Icons";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

function useNavItems(): NavItem[] {
  const { t } = useTranslation();
  const role = useAuth((s) => s.user?.role);
  return useMemo(() => {
    const items: NavItem[] = [
      { to: "/dashboard", label: t("nav.dashboard"), icon: <IconDashboard /> },
      {
        to: "/instagram-accounts",
        label: t("nav.instagramAccounts"),
        icon: <IconAccounts />,
      },
      { to: "/campaigns", label: t("nav.campaigns"), icon: <IconCampaigns /> },
      { to: "/discover", label: t("nav.discover"), icon: <IconTarget /> },
      { to: "/dm-templates", label: t("nav.dmTemplates"), icon: <IconDM /> },
      { to: "/activity", label: t("nav.activity"), icon: <IconActivity /> },
      { to: "/settings", label: t("nav.settings"), icon: <IconSettings /> },
    ];
    if (role === "admin") {
      // Visible only to admin users; the backend re-checks the role on every
      // /api/admin/* call so a stale-cache user with the link can't use it.
      items.push({ to: "/admin", label: t("nav.admin"), icon: <IconShield /> });
    }
    return items;
  }, [t, role]);
}

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const items = useNavItems();
  return (
    <nav aria-label="Primary" className="space-y-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/dashboard"}
          onClick={onNavigate}
          className={({ isActive }) =>
            clsx(
              "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition",
              isActive
                ? "bg-gradient-to-r from-brand-500/20 to-fuchsia-500/10 text-brand-100 shadow-inner shadow-brand-900/20 ring-1 ring-brand-400/30"
                : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
            )
          }
        >
          {({ isActive }) => (
            <>
              <span
                className={clsx(
                  "transition",
                  isActive ? "text-brand-200" : "text-slate-400 group-hover:text-slate-200"
                )}
              >
                {item.icon}
              </span>
              <span className="truncate">{item.label}</span>
              {isActive && (
                <span
                  className="ml-auto h-1.5 w-1.5 rounded-full bg-brand-300 shadow-[0_0_8px_rgba(238,161,255,0.8)]"
                  aria-hidden="true"
                />
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand({ stacked = false }: { stacked?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5">
      <div
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-fuchsia-600 shadow-lg shadow-brand-900/40 ring-1 ring-white/10"
        aria-hidden="true"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <rect x="3.5" y="14" width="3" height="6.5" rx="1" />
          <rect x="9" y="10" width="3" height="10.5" rx="1" />
          <rect x="14.5" y="6" width="3" height="14.5" rx="1" />
          <circle cx="20" cy="4.7" r="2.2" />
        </svg>
      </div>
      <div className={clsx(stacked && "leading-tight")}>
        <div className="text-sm font-semibold leading-tight tracking-tight">
          {t("brand.name")}
        </div>
        <div className="text-[11px] text-slate-400 leading-tight">{t("brand.tagline")}</div>
      </div>
    </div>
  );
}

function UserCard({ onSignOut }: { onSignOut: () => void }) {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  if (!user) return null;
  const display = user.name || user.email;
  return (
    <div className="mt-4 rounded-2xl border border-slate-800/80 bg-slate-900/60 p-3">
      <div className="flex items-center gap-3">
        <Avatar name={display} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-white">{display}</div>
          {user.plan && (
            <div className="text-[11px] uppercase tracking-wider text-brand-300">
              {user.plan}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onSignOut}
          aria-label={t("nav.signOut")}
          className="-mr-1 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-800 hover:text-white"
        >
          <IconSignOut size={16} />
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuth((s) => s.user);
  const clear = useAuth((s) => s.clear);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close the mobile drawer on route changes.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  function signOut(): void {
    clear();
    navigate("/login");
  }

  return (
    <div className="relative flex min-h-screen">
      {/* Page background: soft gradient blobs that stay subtle even on bright tiles. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[420px] overflow-hidden"
      >
        <div className="absolute -top-32 left-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-brand-600/15 blur-3xl" />
        <div className="absolute -top-24 right-0 h-56 w-56 rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-800/80 bg-slate-950/70 p-4 md:flex">
        <div className="px-1.5 pb-6 pt-2">
          <Brand />
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavLinks />
        </div>
        <UserCard onSignOut={signOut} />
      </aside>

      {/* Mobile drawer */}
      <div
        className={clsx(
          "fixed inset-0 z-40 md:hidden",
          drawerOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
        aria-hidden={!drawerOpen}
      >
        <div
          className={clsx(
            "absolute inset-0 bg-slate-950/75 backdrop-blur-sm transition-opacity",
            drawerOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setDrawerOpen(false)}
        />
        <aside
          className={clsx(
            "absolute left-0 top-0 flex h-full w-72 max-w-[88%] flex-col border-r border-slate-800 bg-slate-950 p-4 shadow-2xl transition-transform",
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="mb-5 flex items-center justify-between">
            <Brand />
            <button
              type="button"
              aria-label={t("nav.closeMenu")}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
              onClick={() => setDrawerOpen(false)}
            >
              <IconClose />
            </button>
          </div>
          <NavLinks onNavigate={() => setDrawerOpen(false)} />
          <div className="mt-auto space-y-3 border-t border-slate-800 pt-4">
            <div className="px-1">
              <LanguageSwitcher variant="block" />
            </div>
            <UserCard onSignOut={signOut} />
          </div>
        </aside>
      </div>

      <main className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-800/80 bg-slate-950/85 px-3 py-2.5 backdrop-blur md:hidden">
          <button
            type="button"
            aria-label={t("nav.openMenu")}
            className="-ml-1 rounded-lg p-2 text-slate-300 hover:bg-slate-800 hover:text-white"
            onClick={() => setDrawerOpen(true)}
          >
            <IconMenu />
          </button>
          <Brand />
          {user ? (
            <Avatar name={user.name || user.email} size="sm" />
          ) : (
            <span className="h-8 w-8" />
          )}
        </header>

        {/* Desktop top bar */}
        <header className="hidden items-center justify-between gap-4 border-b border-slate-800/80 bg-slate-950/40 px-6 py-2.5 backdrop-blur md:flex">
          <div className="min-w-0 truncate text-xs text-slate-400">{user?.email}</div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
          </div>
        </header>

        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          <SubscriptionBanner />
          <IgAccountAttentionBanner />
          {children}
        </div>
        <footer className="border-t border-slate-800/60 px-6 py-3 text-center text-xs text-slate-500">
          {t("nav.support")}{" "}
          <a
            href="mailto:support@musoboost.com"
            className="text-slate-400 underline-offset-2 hover:text-white hover:underline transition-colors"
          >
            support@musoboost.com
          </a>
        </footer>
      </main>
    </div>
  );
}
